import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { classNames } from 'primereact/utils';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import globalSettingsStores from '../../../../stores/settings-stores';
import appApi from '../../../../api/app-api';
import { UserSummaryType } from '../../../../api/types/user/user-summary.type';
import globalUserPage from '../../../../stores/global-user-page';
import { toast } from 'react-toastify';
import { Tooltip } from 'primereact/tooltip';
import { PaymentInstallmentStatus } from '../../../../api/types/payments/installments/entities';
import { displayErrorText } from '../../../../api/errors/api-error-codes';
import { CreditCardTokenEntity } from '../../../../api/types/students/credit-card-tokens/credit-card-token-entity';
import { CreditCardSelection } from './components/credit-card-selection';
import { AddNewCardDialog } from './components/add-new-card-dialog';

// Payment method types
export enum PaymentMethod {
  CreditCard = 'credit_card',
  BankTransfer = 'bank_transfer',
  Check = 'check',
  Scholarship = 'scholarship',
  Cash = 'cash',
  CreditCardInstallments = 'credit_card_installments',
  BankTransferInstallments = 'bank_transfer_installments',
  ExistingCreditCardInstallments = 'existing_credit_card_installments',
  ExistingBankTransferInstallments = 'existing_bank_transfer_installments',
}

export interface AllocationWithAmount {
  allocationId: number;
  paymentServiceName: string;
  maxAmount: number;
  amount: number;
}

interface PaymentMethodSelectionDialogProps {
  visible: boolean;
  onHide: () => void;
  allocations: AllocationWithAmount[];
  user: UserSummaryType;
  onPaymentMethodSelected: (
    method: PaymentMethod,
    allocations: AllocationWithAmount[],
    additionalData?: any,
  ) => void;
}

export const PaymentMethodSelectionDialog: React.FC<
  PaymentMethodSelectionDialogProps
> = ({ visible, onHide, allocations, user, onPaymentMethodSelected }) => {
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
    null,
  );
  const [allocationAmounts, setAllocationAmounts] = useState<
    AllocationWithAmount[]
  >([]);
  const [loading, setLoading] = useState(false);

  // Initialize allocation amounts when dialog opens or allocations change
  React.useEffect(() => {
    if (visible && allocations.length > 0) {
      setAllocationAmounts(
        allocations.map((alloc) => ({
          ...alloc,
          amount: alloc.maxAmount, // Default to max amount
        })),
      );
    }
  }, [visible, allocations]);

  // Bank Transfer Form State
  const [bankTransferData, setBankTransferData] = useState({
    bankNumber: '',
    branchNumber: '',
    accountNumber: '',
    transferDate: null as Date | null,
  });

  // Check Form State
  const [checkData, setCheckData] = useState({
    checkNumber: '',
    bankNumber: '',
    branchNumber: '',
    accountNumber: '',
    clearingDate: null as Date | null,
  });

  // Scholarship Form State
  const [scholarshipData, setScholarshipData] = useState({
    scholarshipId: null as number | null,
  });

  // Credit Card Installments Form State
  const [installmentsData, setInstallmentsData] = useState({
    numberOfInstallments: 1 as number,
    selectedCreditCardTokenId: null as number | null,
  });

  // Add New Card Dialog State
  const [showAddNewCardDialog, setShowAddNewCardDialog] = useState(false);

  // Get existing credit card tokens
  const creditCardTokens: CreditCardTokenEntity[] =
    globalUserPage.studentDetails?.creditCardTokens?.filter(
      (token) => token.isActive,
    ) ?? [];

  // Bank Transfer Installments Form State
  const [bankTransferInstallmentsData, setBankTransferInstallmentsData] =
    useState({
      numberOfInstallments: 1 as number,
      bankNumber: '',
      branchNumber: '',
      accountNumber: '',
    });

  // Existing Installment Plan Selection State
  const [
    selectedExistingInstallmentPlanId,
    setSelectedExistingInstallmentPlanId,
  ] = useState<number | null>(null);
  const [
    selectedExistingBankTransferPlanId,
    setSelectedExistingBankTransferPlanId,
  ] = useState<number | null>(null);

  // Get existing installment plans from user details
  const existingCreditCardInstallments =
    globalUserPage.studentDetails?.installments ?? [];
  const existingBankTransferInstallments =
    globalUserPage.studentDetails?.installmentsBankTransfer ?? [];

  // Helper function to calculate the first payment date (20th of the closest month)
  const calculateFirstPaymentDate = (): Date => {
    const today = new Date();
    const currentDay = today.getDate();
    const year = today.getFullYear();
    const month = today.getMonth();

    // If today is before or on the 20th, use the 20th of this month
    // If today is after the 20th, use the 20th of next month
    if (currentDay <= 20) {
      return new Date(year, month, 20);
    } else {
      // Move to next month
      return new Date(year, month + 1, 20);
    }
  };

  const basePaymentMethods = [
    {
      type: PaymentMethod.CreditCard,
      icon: <i className="pi pi-credit-card text-2xl"></i>,
      label: 'כרטיס אשראי',
      description: 'תשלום מאובטח באמצעות כרטיס אשראי',
      color: '#00C853',
    },
    {
      type: PaymentMethod.BankTransfer,
      icon: <i className="pi pi-money-bill text-2xl"></i>,
      label: 'העברה בנקאית',
      description: 'העברה ישירה מחשבון הבנק שלך',
      color: '#2196F3',
    },
    {
      type: PaymentMethod.Check,
      icon: <i className="pi pi-file-edit text-2xl"></i>,
      label: 'המחאות',
      description: 'תשלום באמצעות המחאה',
      color: '#9C27B0',
    },
    {
      type: PaymentMethod.Scholarship,
      icon: <i className="pi pi-bookmark text-2xl"></i>,
      label: 'מלגות',
      description: 'תשלום באמצעות מלגה',
      color: '#FF9800',
    },
    {
      type: PaymentMethod.Cash,
      icon: <i className="pi pi-wallet text-2xl"></i>,
      label: 'מזומן',
      description: 'תשלום במזומן',
      color: '#00C853',
    },
    {
      type: PaymentMethod.CreditCardInstallments,
      icon: <i className="pi pi-calendar text-2xl"></i>,
      label: 'הוראת קבע באשראי',
      description: 'תשלומים חודשיים קבועים בכרטיס אשראי',
      color: '#E91E63',
    },
    {
      type: PaymentMethod.BankTransferInstallments,
      icon: <i className="pi pi-sync text-2xl"></i>,
      label: 'הוראת קבע בנקאית',
      description: 'תשלומים חודשיים קבועים בהעברה בנקאית',
      color: '#3F51B5',
    },
  ];

  // Add existing installment options if available
  const paymentMethods = [
    ...basePaymentMethods,
    ...(existingCreditCardInstallments.length > 0
      ? [
          {
            type: PaymentMethod.ExistingCreditCardInstallments,
            icon: <i className="pi pi-plus-circle text-2xl"></i>,
            label: 'הוסף להוראת קבע באשראי קיימת',
            description: `${existingCreditCardInstallments.length} הוראות קבע פעילות`,
            color: '#C2185B',
          },
        ]
      : []),
    ...(existingBankTransferInstallments.length > 0
      ? [
          {
            type: PaymentMethod.ExistingBankTransferInstallments,
            icon: <i className="pi pi-plus-circle text-2xl"></i>,
            label: 'הוסף להוראת קבע בנקאית קיימת',
            description: `${existingBankTransferInstallments.length} הוראות קבע פעילות`,
            color: '#303F9F',
          },
        ]
      : []),
  ];

  const renderPaymentMethodForm = () => {
    const formContainerStyle = {
      padding: '2rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    };

    const inputStyle = {
      width: '100%',
      marginBottom: '1rem',
    };

    switch (selectedMethod) {
      case PaymentMethod.BankTransfer:
        return (
          <div style={formContainerStyle}>
            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col">
                <label className="mb-2 font-medium">בנק</label>
                <InputText
                  style={inputStyle}
                  value={bankTransferData.bankNumber}
                  onChange={(e) =>
                    setBankTransferData({
                      ...bankTransferData,
                      bankNumber: e.target.value,
                    })
                  }
                  placeholder="הזן מספר בנק"
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-2 font-medium">סניף</label>
                <InputText
                  style={inputStyle}
                  value={bankTransferData.branchNumber}
                  onChange={(e) =>
                    setBankTransferData({
                      ...bankTransferData,
                      branchNumber: e.target.value,
                    })
                  }
                  placeholder="הזן מספר סניף"
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-2 font-medium">חשבון</label>
                <InputText
                  style={inputStyle}
                  value={bankTransferData.accountNumber}
                  onChange={(e) =>
                    setBankTransferData({
                      ...bankTransferData,
                      accountNumber: e.target.value,
                    })
                  }
                  placeholder="הזן מספר חשבון"
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-2 font-medium">תאריך העברה</label>
                <Calendar
                  style={{ direction: 'ltr', ...inputStyle }}
                  value={bankTransferData.transferDate}
                  onChange={(e) =>
                    setBankTransferData({
                      ...bankTransferData,
                      transferDate: e.value as Date,
                    })
                  }
                  inputStyle={{ direction: 'rtl' }}
                  showIcon
                  iconPos={'right'}
                  dateFormat="dd/mm/yy"
                />
              </div>
            </div>
          </div>
        );

      case PaymentMethod.Check:
        return (
          <div style={formContainerStyle}>
            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col">
                <label className="mb-2 font-medium">מספר המחאה</label>
                <InputText
                  style={inputStyle}
                  value={checkData.checkNumber}
                  onChange={(e) =>
                    setCheckData({ ...checkData, checkNumber: e.target.value })
                  }
                  placeholder="הזן מספר המחאה"
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-2 font-medium">בנק</label>
                <InputText
                  style={inputStyle}
                  value={checkData.bankNumber}
                  onChange={(e) =>
                    setCheckData({ ...checkData, bankNumber: e.target.value })
                  }
                  placeholder="הזן מספר בנק"
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-2 font-medium">סניף</label>
                <InputText
                  style={inputStyle}
                  value={checkData.branchNumber}
                  onChange={(e) =>
                    setCheckData({ ...checkData, branchNumber: e.target.value })
                  }
                  placeholder="הזן מספר סניף"
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-2 font-medium">חשבון</label>
                <InputText
                  style={inputStyle}
                  value={checkData.accountNumber}
                  onChange={(e) =>
                    setCheckData({
                      ...checkData,
                      accountNumber: e.target.value,
                    })
                  }
                  placeholder="הזן מספר חשבון"
                />
              </div>
              <div className="flex flex-col col-span-2">
                <label className="mb-2 font-medium">תאריך פירעון</label>
                <Calendar
                  style={{ direction: 'ltr', ...inputStyle }}
                  value={checkData.clearingDate}
                  onChange={(e) =>
                    setCheckData({
                      ...checkData,
                      clearingDate: e.value as Date,
                    })
                  }
                  inputStyle={{ direction: 'rtl' }}
                  showIcon
                  iconPos={'right'}
                  dateFormat="dd/mm/yy"
                />
              </div>
            </div>
          </div>
        );

      case PaymentMethod.Scholarship:
        return (
          <div style={formContainerStyle}>
            <div className="flex flex-col">
              <label className="mb-2 font-medium">סוג מלגה</label>
              <Dropdown
                style={inputStyle}
                value={scholarshipData.scholarshipId}
                options={globalSettingsStores.scholarshipFunders.map(
                  (funder) => ({
                    label: funder.funderName,
                    value: funder.id,
                  }),
                )}
                onChange={(e) =>
                  setScholarshipData({
                    ...scholarshipData,
                    scholarshipId: e.value,
                  })
                }
                placeholder="בחר סוג מלגה"
              />
            </div>
          </div>
        );

      case PaymentMethod.CreditCardInstallments:
        const firstPaymentDate = calculateFirstPaymentDate();
        const totalAmount = allocationAmounts.reduce(
          (sum, alloc) => sum + (alloc.amount || 0),
          0,
        );
        const monthlyPayment =
          installmentsData.numberOfInstallments > 0
            ? totalAmount / installmentsData.numberOfInstallments
            : totalAmount;

        return (
          <div style={formContainerStyle}>
            <div className="flex flex-col gap-4">
              {/* Credit Card Selection */}
              <CreditCardSelection
                creditCardTokens={creditCardTokens}
                selectedCreditCardTokenId={
                  installmentsData.selectedCreditCardTokenId
                }
                onCreditCardChange={(tokenId) =>
                  setInstallmentsData({
                    ...installmentsData,
                    selectedCreditCardTokenId: tokenId,
                  })
                }
                onAddNewCardClick={() => setShowAddNewCardDialog(true)}
              />

              <div className="flex flex-col">
                <label className="mb-2 font-medium">מספר תשלומים</label>
                <Dropdown
                  style={inputStyle}
                  value={installmentsData.numberOfInstallments}
                  options={Array.from({ length: 12 }, (_, i) => ({
                    label: `${i + 1} תשלומים`,
                    value: i + 1,
                  }))}
                  onChange={(e) =>
                    setInstallmentsData({
                      ...installmentsData,
                      numberOfInstallments: e.value,
                    })
                  }
                  placeholder="בחר מספר תשלומים"
                />
              </div>

              {/* Friendly description for the user */}
              <div
                style={{
                  backgroundColor: '#E3F2FD',
                  borderRadius: '8px',
                  padding: '16px',
                  border: '1px solid #90CAF9',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                  }}
                >
                  <i
                    className="pi pi-info-circle"
                    style={{ color: '#1976D2', fontSize: '20px' }}
                  ></i>
                  <span
                    style={{
                      fontWeight: 'bold',
                      color: '#1976D2',
                      fontSize: '16px',
                    }}
                  >
                    פרטי הוראת הקבע
                  </span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    color: '#424242',
                  }}
                >
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>סה"כ לתשלום:</span>
                    <span style={{ fontWeight: 'bold' }}>
                      ₪{totalAmount.toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>מספר תשלומים:</span>
                    <span style={{ fontWeight: 'bold' }}>
                      {installmentsData.numberOfInstallments}
                    </span>
                  </div>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>סכום תשלום חודשי:</span>
                    <span style={{ fontWeight: 'bold', color: '#1976D2' }}>
                      ₪{monthlyPayment.toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      borderTop: '1px solid #90CAF9',
                      paddingTop: '8px',
                      marginTop: '4px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>תאריך חיוב ראשון:</span>
                      <span style={{ fontWeight: 'bold' }}>
                        {firstPaymentDate.toLocaleDateString('he-IL')}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '4px',
                      }}
                    >
                      <span>יום חיוב בכל חודש:</span>
                      <span style={{ fontWeight: 'bold' }}>20 לחודש</span>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px',
                    backgroundColor: '#FFF3E0',
                    borderRadius: '6px',
                    border: '1px solid #FFB74D',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                    }}
                  >
                    <i
                      className="pi pi-calendar"
                      style={{
                        color: '#F57C00',
                        fontSize: '16px',
                        marginTop: '2px',
                      }}
                    ></i>
                    <div style={{ fontSize: '14px', color: '#E65100' }}>
                      <strong>לידיעתך:</strong> לאחר אישור ההוראה, יופיעו
                      בדשבורד הוראות הקבע שלך פירוט של כל התשלומים החודשיים,
                      כולל תאריכי החיוב והסכומים.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case PaymentMethod.BankTransferInstallments:
        const bankInstallmentsFirstPaymentDate = calculateFirstPaymentDate();
        const bankInstallmentsTotalAmount = allocationAmounts.reduce(
          (sum, alloc) => sum + (alloc.amount || 0),
          0,
        );
        const bankInstallmentsMonthlyPayment =
          bankTransferInstallmentsData.numberOfInstallments > 0
            ? bankInstallmentsTotalAmount /
              bankTransferInstallmentsData.numberOfInstallments
            : bankInstallmentsTotalAmount;

        return (
          <div style={formContainerStyle}>
            <div className="flex flex-col gap-4">
              {/* Bank Details Section */}
              <div
                style={{
                  backgroundColor: '#E8EAF6',
                  borderRadius: '8px',
                  padding: '16px',
                  border: '1px solid #C5CAE9',
                  marginBottom: '8px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '16px',
                  }}
                >
                  <i
                    className="pi pi-building"
                    style={{ color: '#3F51B5', fontSize: '20px' }}
                  ></i>
                  <span
                    style={{
                      fontWeight: 'bold',
                      color: '#3F51B5',
                      fontSize: '16px',
                    }}
                  >
                    פרטי חשבון בנק
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="mb-2 font-medium">בנק</label>
                    <InputText
                      style={inputStyle}
                      value={bankTransferInstallmentsData.bankNumber}
                      onChange={(e) =>
                        setBankTransferInstallmentsData({
                          ...bankTransferInstallmentsData,
                          bankNumber: e.target.value,
                        })
                      }
                      placeholder="הזן מספר בנק"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="mb-2 font-medium">סניף</label>
                    <InputText
                      style={inputStyle}
                      value={bankTransferInstallmentsData.branchNumber}
                      onChange={(e) =>
                        setBankTransferInstallmentsData({
                          ...bankTransferInstallmentsData,
                          branchNumber: e.target.value,
                        })
                      }
                      placeholder="הזן מספר סניף"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="mb-2 font-medium">חשבון</label>
                    <InputText
                      style={inputStyle}
                      value={bankTransferInstallmentsData.accountNumber}
                      onChange={(e) =>
                        setBankTransferInstallmentsData({
                          ...bankTransferInstallmentsData,
                          accountNumber: e.target.value,
                        })
                      }
                      placeholder="הזן מספר חשבון"
                    />
                  </div>
                </div>
              </div>

              {/* Number of Installments */}
              <div className="flex flex-col">
                <label className="mb-2 font-medium">מספר תשלומים</label>
                <Dropdown
                  style={inputStyle}
                  value={bankTransferInstallmentsData.numberOfInstallments}
                  options={Array.from({ length: 12 }, (_, i) => ({
                    label: `${i + 1} תשלומים`,
                    value: i + 1,
                  }))}
                  onChange={(e) =>
                    setBankTransferInstallmentsData({
                      ...bankTransferInstallmentsData,
                      numberOfInstallments: e.value,
                    })
                  }
                  placeholder="בחר מספר תשלומים"
                />
              </div>

              {/* Friendly description for the user */}
              <div
                style={{
                  backgroundColor: '#E3F2FD',
                  borderRadius: '8px',
                  padding: '16px',
                  border: '1px solid #90CAF9',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                  }}
                >
                  <i
                    className="pi pi-info-circle"
                    style={{ color: '#1976D2', fontSize: '20px' }}
                  ></i>
                  <span
                    style={{
                      fontWeight: 'bold',
                      color: '#1976D2',
                      fontSize: '16px',
                    }}
                  >
                    פרטי הוראת הקבע
                  </span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    color: '#424242',
                  }}
                >
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>סה"כ לתשלום:</span>
                    <span style={{ fontWeight: 'bold' }}>
                      ₪{bankInstallmentsTotalAmount.toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>מספר תשלומים:</span>
                    <span style={{ fontWeight: 'bold' }}>
                      {bankTransferInstallmentsData.numberOfInstallments}
                    </span>
                  </div>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>סכום תשלום חודשי:</span>
                    <span style={{ fontWeight: 'bold', color: '#1976D2' }}>
                      ₪{bankInstallmentsMonthlyPayment.toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      borderTop: '1px solid #90CAF9',
                      paddingTop: '8px',
                      marginTop: '4px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>תאריך חיוב ראשון:</span>
                      <span style={{ fontWeight: 'bold' }}>
                        {bankInstallmentsFirstPaymentDate.toLocaleDateString(
                          'he-IL',
                        )}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '4px',
                      }}
                    >
                      <span>יום חיוב בכל חודש:</span>
                      <span style={{ fontWeight: 'bold' }}>20 לחודש</span>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px',
                    backgroundColor: '#FFF3E0',
                    borderRadius: '6px',
                    border: '1px solid #FFB74D',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                    }}
                  >
                    <i
                      className="pi pi-calendar"
                      style={{
                        color: '#F57C00',
                        fontSize: '16px',
                        marginTop: '2px',
                      }}
                    ></i>
                    <div style={{ fontSize: '14px', color: '#E65100' }}>
                      <strong>לידיעתך:</strong> לאחר אישור ההוראה, יופיעו
                      בדשבורד הוראות הקבע שלך פירוט של כל התשלומים החודשיים,
                      כולל תאריכי החיוב והסכומים.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case PaymentMethod.ExistingCreditCardInstallments:
        const newAmountToAdd = allocationAmounts.reduce(
          (sum, alloc) => sum + (alloc.amount || 0),
          0,
        );
        const selectedCreditCardPlan = existingCreditCardInstallments.find(
          (plan) =>
            plan.installmentPlan.id === selectedExistingInstallmentPlanId,
        );
        const remainingPaymentsCreditCard = selectedCreditCardPlan
          ? selectedCreditCardPlan.installment.filter(
              (item) => item.status === PaymentInstallmentStatus.PENDING,
            ).length
          : 0;
        const currentMonthlyPaymentCreditCard = selectedCreditCardPlan
          ? selectedCreditCardPlan.installmentPlan.totalAmount /
            selectedCreditCardPlan.installmentPlan.numberOfInstallments
          : 0;
        const additionalMonthlyPaymentCreditCard =
          remainingPaymentsCreditCard > 0
            ? newAmountToAdd / remainingPaymentsCreditCard
            : newAmountToAdd;
        const newMonthlyPaymentCreditCard =
          currentMonthlyPaymentCreditCard + additionalMonthlyPaymentCreditCard;

        return (
          <div style={formContainerStyle}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col">
                <label className="mb-2 font-medium">בחר הוראת קבע קיימת</label>
                <Dropdown
                  style={inputStyle}
                  value={selectedExistingInstallmentPlanId}
                  options={existingCreditCardInstallments.map((plan) => {
                    const remaining = plan.installment.filter(
                      (item) =>
                        item.status === PaymentInstallmentStatus.PENDING,
                    ).length;
                    const monthlyPayment =
                      plan.installmentPlan.totalAmount /
                      plan.installmentPlan.numberOfInstallments;
                    return {
                      label: `₪${monthlyPayment.toFixed(0)}/חודש - ${remaining} תשלומים נותרו - נוצר ב-${new Date(plan.installmentPlan.createdAt).toLocaleDateString('he-IL')}`,
                      value: plan.installmentPlan.id,
                    };
                  })}
                  onChange={(e) =>
                    setSelectedExistingInstallmentPlanId(e.value)
                  }
                  placeholder="בחר הוראת קבע"
                />
              </div>

              {selectedCreditCardPlan && (
                <div
                  style={{
                    backgroundColor: '#FCE4EC',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #F48FB1',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px',
                    }}
                  >
                    <i
                      className="pi pi-info-circle"
                      style={{ color: '#C2185B', fontSize: '20px' }}
                    ></i>
                    <span
                      style={{
                        fontWeight: 'bold',
                        color: '#C2185B',
                        fontSize: '16px',
                      }}
                    >
                      פרטי הוספה להוראת קבע
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      color: '#424242',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>תשלומים נותרים:</span>
                      <span style={{ fontWeight: 'bold' }}>
                        {remainingPaymentsCreditCard}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>תשלום חודשי נוכחי:</span>
                      <span style={{ fontWeight: 'bold' }}>
                        ₪{currentMonthlyPaymentCreditCard.toFixed(2)}
                      </span>
                    </div>
                    <div
                      style={{
                        borderTop: '1px solid #F48FB1',
                        paddingTop: '8px',
                        marginTop: '4px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span>סכום להוספה:</span>
                        <span style={{ fontWeight: 'bold' }}>
                          ₪{newAmountToAdd.toFixed(2)}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginTop: '4px',
                        }}
                      >
                        <span>תוספת לתשלום חודשי:</span>
                        <span style={{ fontWeight: 'bold', color: '#C2185B' }}>
                          +₪{additionalMonthlyPaymentCreditCard.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div
                      style={{
                        borderTop: '2px solid #C2185B',
                        paddingTop: '8px',
                        marginTop: '4px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span style={{ fontWeight: 'bold' }}>
                          תשלום חודשי חדש:
                        </span>
                        <span
                          style={{
                            fontWeight: 'bold',
                            color: '#C2185B',
                            fontSize: '18px',
                          }}
                        >
                          ₪{newMonthlyPaymentCreditCard.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case PaymentMethod.ExistingBankTransferInstallments:
        const newAmountToAddBank = allocationAmounts.reduce(
          (sum, alloc) => sum + (alloc.amount || 0),
          0,
        );
        const selectedBankTransferPlan = existingBankTransferInstallments.find(
          (plan) =>
            plan.installmentPlan.id === selectedExistingBankTransferPlanId,
        );
        const remainingPaymentsBank = selectedBankTransferPlan
          ? selectedBankTransferPlan.installment.filter(
              (item) => item.status === PaymentInstallmentStatus.PENDING,
            ).length
          : 0;
        const currentMonthlyPaymentBank = selectedBankTransferPlan
          ? selectedBankTransferPlan.installmentPlan.totalAmount /
            selectedBankTransferPlan.installmentPlan.numberOfInstallments
          : 0;
        const additionalMonthlyPaymentBank =
          remainingPaymentsBank > 0
            ? newAmountToAddBank / remainingPaymentsBank
            : newAmountToAddBank;
        const newMonthlyPaymentBank =
          currentMonthlyPaymentBank + additionalMonthlyPaymentBank;

        return (
          <div style={formContainerStyle}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col">
                <label className="mb-2 font-medium">בחר הוראת קבע קיימת</label>
                <Dropdown
                  style={inputStyle}
                  value={selectedExistingBankTransferPlanId}
                  options={existingBankTransferInstallments.map((plan) => {
                    const remaining = plan.installment.filter(
                      (item) =>
                        item.status === PaymentInstallmentStatus.PENDING,
                    ).length;
                    const monthlyPayment =
                      plan.installmentPlan.totalAmount /
                      plan.installmentPlan.numberOfInstallments;
                    return {
                      label: `₪${monthlyPayment.toFixed(0)}/חודש - ${remaining} תשלומים נותרו - נוצר ב-${new Date(plan.installmentPlan.createdAt).toLocaleDateString('he-IL')}`,
                      value: plan.installmentPlan.id,
                    };
                  })}
                  onChange={(e) =>
                    setSelectedExistingBankTransferPlanId(e.value)
                  }
                  placeholder="בחר הוראת קבע"
                />
              </div>

              {selectedBankTransferPlan && (
                <div
                  style={{
                    backgroundColor: '#E8EAF6',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #9FA8DA',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px',
                    }}
                  >
                    <i
                      className="pi pi-info-circle"
                      style={{ color: '#303F9F', fontSize: '20px' }}
                    ></i>
                    <span
                      style={{
                        fontWeight: 'bold',
                        color: '#303F9F',
                        fontSize: '16px',
                      }}
                    >
                      פרטי הוספה להוראת קבע
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      color: '#424242',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>תשלומים נותרים:</span>
                      <span style={{ fontWeight: 'bold' }}>
                        {remainingPaymentsBank}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>תשלום חודשי נוכחי:</span>
                      <span style={{ fontWeight: 'bold' }}>
                        ₪{currentMonthlyPaymentBank.toFixed(2)}
                      </span>
                    </div>
                    <div
                      style={{
                        borderTop: '1px solid #9FA8DA',
                        paddingTop: '8px',
                        marginTop: '4px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span>סכום להוספה:</span>
                        <span style={{ fontWeight: 'bold' }}>
                          ₪{newAmountToAddBank.toFixed(2)}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginTop: '4px',
                        }}
                      >
                        <span>תוספת לתשלום חודשי:</span>
                        <span style={{ fontWeight: 'bold', color: '#303F9F' }}>
                          +₪{additionalMonthlyPaymentBank.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div
                      style={{
                        borderTop: '2px solid #303F9F',
                        paddingTop: '8px',
                        marginTop: '4px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span style={{ fontWeight: 'bold' }}>
                          תשלום חודשי חדש:
                        </span>
                        <span
                          style={{
                            fontWeight: 'bold',
                            color: '#303F9F',
                            fontSize: '18px',
                          }}
                        >
                          ₪{newMonthlyPaymentBank.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const [hasInvalidAmount, setHasInvalidAmount] = useState<
    AllocationWithAmount[]
  >([]);

  useEffect(() => {
    const hasInvalidAmount = allocationAmounts.filter(
      (alloc) =>
        !alloc.amount || alloc.amount <= 0 || alloc.amount > alloc.maxAmount,
    );

    setHasInvalidAmount(hasInvalidAmount);
  }, [allocationAmounts]);

  const handleSubmit = async () => {
    if (!selectedMethod || allocationAmounts.length === 0) return;

    // Validate that all amounts are valid
    const hasInvalidAmount = allocationAmounts.filter(
      (alloc) =>
        !alloc.amount || alloc.amount <= 0 || alloc.amount > alloc.maxAmount,
    );
    if (hasInvalidAmount.length > 0) {
      // Show error message
      toast.warn(
        `שים לב, לא ניתן לשלם עבור ${hasInvalidAmount[0].paymentServiceName} יותר מ ${hasInvalidAmount[0].maxAmount}`,
      );
      console.error('Invalid amounts detected');
      return;
    }

    try {
      setLoading(true);
      switch (selectedMethod) {
        case PaymentMethod.CreditCard:
          // Keep existing credit card flow
          onPaymentMethodSelected(selectedMethod, allocationAmounts);
          break;

        case PaymentMethod.BankTransfer:
          if (
            !bankTransferData.bankNumber ||
            !bankTransferData.branchNumber ||
            !bankTransferData.accountNumber ||
            !bankTransferData.transferDate
          ) {
            // Show error message
            setLoading(false);
            return;
          }
          await appApi.createPaymentAllocation(user.id, {
            allocations: allocationAmounts.map((alloc) => ({
              allocationId: alloc.allocationId,
              amount: alloc.amount,
            })),
            paymentMethod: 'bank_transfer',
            paymentDetails: {
              bankNumber: bankTransferData.bankNumber,
              branchNumber: bankTransferData.branchNumber,
              accountNumber: bankTransferData.accountNumber,
              transferDate: bankTransferData.transferDate,
            },
          });
          break;

        case PaymentMethod.Check:
          if (
            !checkData.checkNumber ||
            !checkData.bankNumber ||
            !checkData.branchNumber ||
            !checkData.accountNumber
          ) {
            // Show error message
            setLoading(false);
            return;
          }
          await appApi.createPaymentAllocation(user.id, {
            allocations: allocationAmounts.map((alloc) => ({
              allocationId: alloc.allocationId,
              amount: alloc.amount,
            })),
            paymentMethod: 'check',
            paymentDetails: {
              checkNumber: checkData.checkNumber,
              bankNumber: checkData.bankNumber,
              branchNumber: checkData.branchNumber,
              accountNumber: checkData.accountNumber,
              dueDate: checkData.clearingDate || undefined,
            },
          });
          break;

        case PaymentMethod.Scholarship:
          if (!scholarshipData.scholarshipId) {
            // Show error message
            setLoading(false);
            return;
          }
          await appApi.createPaymentAllocation(user.id, {
            allocations: allocationAmounts.map((alloc) => ({
              allocationId: alloc.allocationId,
              amount: alloc.amount,
            })),
            paymentMethod: 'scholarship',
            paymentDetails: {
              scholarshipId: scholarshipData.scholarshipId,
            },
          });
          break;

        case PaymentMethod.Cash:
          await appApi.createPaymentAllocation(user.id, {
            allocations: allocationAmounts.map((alloc) => ({
              allocationId: alloc.allocationId,
              amount: alloc.amount,
            })),
            paymentMethod: 'cash',
          });
          break;

        case PaymentMethod.CreditCardInstallments:
          if (
            !installmentsData.numberOfInstallments ||
            installmentsData.numberOfInstallments < 1 ||
            installmentsData.numberOfInstallments > 12
          ) {
            toast.warn('יש לבחור מספר תשלומים בין 1 ל-12');
            setLoading(false);
            return;
          }
          if (!installmentsData.selectedCreditCardTokenId) {
            toast.warn('יש לבחור כרטיס אשראי');
            setLoading(false);
            return;
          }
          const firstPaymentDate = calculateFirstPaymentDate();
          await appApi.createPaymentInstallments(user.id, {
            allocations: allocationAmounts.map((alloc) => ({
              allocationId: alloc.allocationId,
              amount: alloc.amount,
            })),
            numberOfInstallments: installmentsData.numberOfInstallments,
            firstPaymentDate: firstPaymentDate.toISOString(),
            paymentDayOfMonth: 20,
            creditCardTokenId: installmentsData.selectedCreditCardTokenId,
          });
          break;

        case PaymentMethod.BankTransferInstallments:
          if (
            !bankTransferInstallmentsData.numberOfInstallments ||
            bankTransferInstallmentsData.numberOfInstallments < 1 ||
            bankTransferInstallmentsData.numberOfInstallments > 12
          ) {
            toast.warn('יש לבחור מספר תשלומים בין 1 ל-12');
            setLoading(false);
            return;
          }
          if (
            !bankTransferInstallmentsData.bankNumber ||
            !bankTransferInstallmentsData.branchNumber ||
            !bankTransferInstallmentsData.accountNumber
          ) {
            toast.warn('יש למלא את כל פרטי חשבון הבנק');
            setLoading(false);
            return;
          }
          const bankInstallmentsFirstPaymentDate = calculateFirstPaymentDate();
          await appApi.createBankTransferInstallments(user.id, {
            allocations: allocationAmounts.map((alloc) => ({
              allocationId: alloc.allocationId,
              amount: alloc.amount,
            })),
            numberOfInstallments:
              bankTransferInstallmentsData.numberOfInstallments,
            firstPaymentDate: bankInstallmentsFirstPaymentDate.toISOString(),
            paymentDayOfMonth: 20,
            bankNumber: bankTransferInstallmentsData.bankNumber,
            branchNumber: bankTransferInstallmentsData.branchNumber,
            accountNumber: bankTransferInstallmentsData.accountNumber,
          });
          break;

        case PaymentMethod.ExistingCreditCardInstallments:
          if (!selectedExistingInstallmentPlanId) {
            toast.warn('יש לבחור הוראת קבע קיימת');
            setLoading(false);
            return;
          }
          const existingCreditCardInstallmentRes =
            await appApi.addToExistingInstallmentPlan(
              selectedExistingInstallmentPlanId,
              user.id,
              allocationAmounts.map((alloc) => ({
                allocationId: alloc.allocationId,
                amount: alloc.amount,
              })),
            );
          if (existingCreditCardInstallmentRes.data.errCode) {
            toast.warn(
              displayErrorText(existingCreditCardInstallmentRes.data.errCode),
            );
          } else {
            toast.success('שירותי התשלום נוספו להוראת הקבע בהצלחה');
          }

          break;

        case PaymentMethod.ExistingBankTransferInstallments:
          if (!selectedExistingBankTransferPlanId) {
            toast.warn('יש לבחור הוראת קבע קיימת');
            setLoading(false);
            return;
          }
          const existingInstallmentBankTransferRes =
            await appApi.addToExistingInstallmentPlanBankTransfer(
              selectedExistingBankTransferPlanId,
              user.id,
              allocationAmounts.map((alloc) => ({
                allocationId: alloc.allocationId,
                amount: alloc.amount,
              })),
            );

          if (existingInstallmentBankTransferRes.data.errCode) {
            toast.warn(
              displayErrorText(existingInstallmentBankTransferRes.data.errCode),
            );
          } else {
            toast.success('שירותי התשלום נוספו להוראת הקבע בהצלחה');
          }

          break;
      }
      // Refresh user details to get updated payment services
      const updatedUserDetails = await appApi.getUserStudentDetails(user.id);
      globalUserPage.studentDetails = updatedUserDetails.data.body;

      onHide();
      //setLoading(false);
    } catch (error) {
      console.error('Error processing payment:', error);
      setLoading(false);
      // Show error message
    }
  };

  return (
    <Dialog
      header="בחירת אמצעי תשלום"
      visible={visible}
      onHide={onHide}
      style={{ width: '95vw', maxWidth: '1600px' }}
      footer={
        selectedMethod && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'end',
              width: '100%',
              gap: '16px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                width: '100%',
                maxHeight: '300px',
                overflowY: 'auto',
                padding: '8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
              }}
            >
              <label className="font-medium" style={{ fontSize: '16px' }}>
                סכומים לתשלום:
              </label>
              {allocationAmounts.map((allocation, index) => (
                <div
                  key={allocation.allocationId}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '8px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      fontWeight: 500,
                      textAlign: 'right',
                    }}
                  >
                    {allocation.paymentServiceName}
                  </span>

                  <InputNumber
                    value={allocation.amount}
                    prefix={'₪'}
                    inputStyle={{ textAlign: 'right' }}
                    onChange={(e) => {
                      const newAmounts = [...allocationAmounts];
                      newAmounts[index] = {
                        ...newAmounts[index],
                        amount: e.value || 0,
                      };
                      const total = newAmounts.reduce(
                        (sum, item) => sum + item.amount,
                        0,
                      );
                      if (total > allocation.maxAmount) {
                        toast.info(
                          `לא ניתן לחרוג לשרות התשלום ${allocation.paymentServiceName} יותר מ ${allocation.maxAmount}`,
                        );
                      }
                      setAllocationAmounts(newAmounts);
                    }}
                    /*mode="currency"
                    currency="ILS"*/
                    locale="he-IL"
                    /*max={allocation.maxAmount}*/
                    min={0}
                    style={{ width: '200px', direction: 'ltr' }}
                  />
                  <span
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      const newAmounts = [...allocationAmounts];
                      newAmounts[index] = {
                        ...newAmounts[index],
                        amount: 0,
                      };
                      setAllocationAmounts(newAmounts);
                    }}
                    data-pr-tooltip="לחץ לניכוי"
                    data-pr-position="top"
                  >
                    <i
                      className="pi pi-times-circle"
                      style={{ color: '#666' }}
                    />
                  </span>
                  <Tooltip target="[data-pr-tooltip]" />

                  <span style={{ fontSize: '12px', color: '#666' }}>
                    מתוך ₪{allocation.maxAmount}
                  </span>
                </div>
              ))}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '8px',
                  borderTop: '2px solid #2196F3',
                  fontWeight: 'bold',
                }}
              >
                <span>סה"כ:</span>
                <span>
                  ₪
                  {allocationAmounts
                    .reduce((sum, alloc) => sum + (alloc.amount || 0), 0)
                    .toFixed(2)}
                </span>
              </div>
            </div>
            <Button
              label="המשך לתשלום"
              icon="pi pi-check"
              loading={loading}
              disabled={hasInvalidAmount.length > 0}
              tooltip={
                hasInvalidAmount.length > 0
                  ? `שים לב, לא ניתן לשלם עבור השרות ${hasInvalidAmount[0].paymentServiceName} יותר מ ${hasInvalidAmount[0].maxAmount}`
                  : `לחץ לתשלום`
              }
              tooltipOptions={{ position: 'top', showOnDisabled: true }}
              onClick={handleSubmit}
              className="p-button-raised"
            />
          </div>
        )
      }
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '12px',
          marginBottom: '24px',
          width: '100%',
        }}
      >
        {paymentMethods.map((method) => {
          const isSelected = selectedMethod === method.type;
          return (
            <div
              key={method.type}
              onClick={() => setSelectedMethod(method.type)}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '12px',
                padding: '14px 16px',
                backgroundColor: isSelected ? `${method.color}08` : '#ffffff',
                border: `2px solid ${isSelected ? method.color : '#e5e7eb'}`,
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: isSelected
                  ? `0 2px 8px ${method.color}25`
                  : '0 1px 3px rgba(0,0,0,0.05)',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = `${method.color}80`;
                  e.currentTarget.style.backgroundColor = `${method.color}05`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }
              }}
            >
              {/* Checkbox indicator on the left */}
              <div
                style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  border: `2px solid ${isSelected ? method.color : '#d1d5db'}`,
                  backgroundColor: isSelected ? method.color : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.2s ease',
                }}
              >
                {isSelected && (
                  <i
                    className="pi pi-check"
                    style={{ color: 'white', fontSize: '12px' }}
                  />
                )}
              </div>

              {/* Text content in the middle */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: '15px',
                    color: isSelected ? method.color : '#374151',
                    marginBottom: '2px',
                    transition: 'color 0.2s ease',
                  }}
                >
                  {method.label}
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {method.description}
                </div>
              </div>

              {/* Icon on the right */}
              <div
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '10px',
                  backgroundColor: isSelected
                    ? `${method.color}15`
                    : `${method.color}10`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.2s ease',
                  color: method.color,
                }}
              >
                {method.icon}
              </div>
            </div>
          );
        })}
      </div>

      {selectedMethod && (
        <div className="mt-6">
          <h3 className="text-xl font-bold mb-4">פרטי תשלום</h3>
          {renderPaymentMethodForm()}
        </div>
      )}

      {/* Add New Card Dialog */}
      <AddNewCardDialog
        visible={showAddNewCardDialog}
        onHide={() => setShowAddNewCardDialog(false)}
        userId={user.id}
        onSuccess={() => {
          // Credit card tokens are refreshed inside the dialog
          setShowAddNewCardDialog(false);
        }}
      />
    </Dialog>
  );
};
